import {
  CalendarOutlined,
  DollarOutlined,
  IdcardOutlined,
  NumberOutlined,
  SyncOutlined,
  UserOutlined,
} from '@ant-design/icons'
import { DateField, List, useTable } from '@refinedev/antd'
import {
  CrudFilters,
  getDefaultFilter,
  IResourceComponentsProps,
} from '@refinedev/core'
import { Badge, Table } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import { FC, useCallback, useEffect, useMemo, useState } from 'react'

import dayjs from 'dayjs'
import PaymentStatus from '../../types/paymentStatus.type'
import { useIsAdminRole } from '../../utils/hooks/useIdentityAdmin.hook'
import { INVOICE_STATUS_NAMES } from '../../utils/invoiceStatus'
import { paymentStatusColors } from '../../utils/paymentStatus.utils'
import { formatDate } from '../components/constants'
import { DownloadAttachmentsButton } from '../components/downloadAttachmentsButton'
import type { Row } from '../components/interface'
import { cornerBadgeOnCell, NewTag } from '../components/newTag'
import { StatusFilter } from '../components/StatusFilter'
import { TableForm } from '../crypto-withdrawal/tableForm'
import '../styles/index.css'
import { EditInvoicePayable } from './EditInvoicePayableV2'

interface InvoicePayablesSearchProps {
  searchValue: string
  createdAt?: Date[] | null
  status: PaymentStatus
}

export const InvoicePayablesList: FC<IResourceComponentsProps> = () => {
  const [editingId, setEditingId] = useState<number>()
  const isAdminRole = useIsAdminRole()
  const wiseButtonsAvailable = false

  const {
    tableProps,
    searchFormProps,
    filters,
    tableQueryResult: { refetch: refetchList },
  } = useTable<Row>({
    syncWithLocation: true,
    pagination: {
      pageSize: 10,
    },
    onSearch: (data: unknown) => {
      const params = data as InvoicePayablesSearchProps
      const { searchValue, createdAt, status } = params
      const tz = Intl.DateTimeFormat().resolvedOptions().timeZone
      const filters: CrudFilters = [
        {
          field: '',
          operator: 'contains',
          value: searchValue,
        },
        {
          field: '_status',
          operator: 'eq',
          value: PaymentStatus[status] ? status : undefined,
        },
        {
          field: '',
          operator: 'gte',
          value: createdAt
            ? dayjs(createdAt[0])
                .startOf('day')
                .toISOString()
            : undefined,
        },
        {
          field: '',
          operator: 'lte',
          value: createdAt
            ? dayjs(createdAt[1])
                .endOf('day')
                .toISOString()
            : undefined,
        },
        {
          field: '_timeZone',
          operator: 'eq',
          value: tz,
        },
      ]

      return filters
    },
  })

  const [newViewedIds, setNewViewedIds] = useState<number[]>([])

  useEffect(() => {
    setNewViewedIds([])
  }, [tableProps.dataSource])

  const onClose = useCallback(() => {
    setEditingId(undefined)
  }, [])

  const createdAt = useMemo(() => {
    const start = getDefaultFilter('', filters, 'gte')
    const end = getDefaultFilter('', filters, 'lte')

    if (start && end) {
      const startFrom = dayjs(start)
      const endAt = dayjs(end)

      return [startFrom, endAt]
    }

    return undefined
  }, [filters])

  const statusFilterValue = getDefaultFilter(
    '_status',
    filters
  ) as PaymentStatus

  const columns: ColumnsType<Row> = [
    {
      dataIndex: 'id',
      sorter: true,
      title: (
        <span className={'column-header'}>
          <IdcardOutlined />
          &nbsp;ID
        </span>
      ),
    },
    {
      dataIndex: ['paymentRequest', 'id'],
      title: (
        <span className={'column-header'}>
          <NumberOutlined />
          &nbsp;Trans. Ref.
        </span>
      ),
    },
    {
      dataIndex: ['paymentRequest', 'customer', 'fullName'],
      title: (
        <span className={'column-header'}>
          <UserOutlined />
          &nbsp;Customer
        </span>
      ),
    },
    {
      dataIndex: ['paymentRequest', 'company', 'name'],
      sorter: true,
      title: (
        <span className={'column-header'}>
          <UserOutlined />
          &nbsp;Beneficiary
        </span>
      ),
    },
    {
      dataIndex: 'status',
      title: (
        <span className={'column-header'}>
          <SyncOutlined />
          &nbsp;Status
        </span>
      ),
      sorter: true,
      render: (value: string) => (
        <Badge
          color={paymentStatusColors[value]}
          text={INVOICE_STATUS_NAMES[value]}
        />
      ),
    },
    {
      dataIndex: ['paymentRequest', 'dateOfTransaction'],
      title: (
        <span className={'column-header'}>
          <CalendarOutlined />
          &nbsp;Date
        </span>
      ),
      sorter: true,
      render: (value: string) => (
        <DateField value={value} format={formatDate} />
      ),
    },
    {
      dataIndex: ['paymentRequest', 'totalSendAmount'],
      title: (
        <span className={'column-header'}>
          <DollarOutlined />
          &nbsp;Amount
        </span>
      ),
      render: (value) => {
        // if (record.svPayment) {
        return value
        // }
        // return amountListFormatter(value);
      },
    },
    {
      dataIndex: ['paymentRequest', 'currencyCode'],
      title: (
        <span className={'column-header'}>
          <DollarOutlined />
          &nbsp;Currency
        </span>
      ),
      render: (_, record: any) => (
        <span>{record?.paymentRequest?.paymentCurrency?.symbol}</span>
      ),
    },
    {
      dataIndex: ['createdAt'],
      title: (
        <span className={'column-header'}>
          <CalendarOutlined />
          &nbsp;Created
        </span>
      ),
      sorter: true,
      render: (value: string) => (
        <DateField value={value} format={formatDate} />
      ),
    },
    {
      dataIndex: 'documentName',
      onCell: cornerBadgeOnCell(),
      render: (value: string, data) => (
        <>
          {value && (
            <DownloadAttachmentsButton entity={'invoice_payables'} id={data.id} />
          )}
          {data['rowNotViewed'] && !newViewedIds.includes(data.id as number) && <NewTag />}
        </>
      ),
    },
  ]

  return (
    <>
      <List title='Invoices (Payables)'>
        <TableForm
          {...searchFormProps}
          initialValues={{
            searchValue: getDefaultFilter('', filters),
            createdAt,
          }}
          onSubmit={searchFormProps.form?.submit}
        />
        <StatusFilter {...searchFormProps} defaultValue={statusFilterValue} />
        <Table
          {...tableProps}
          rowKey='id'
          columns={columns as any}
          dataSource={tableProps.dataSource}
          onRow={(record) => {
            return {
              onClick: () => {
                setEditingId(Number(record.id))
                if (record['rowNotViewed']) {
                  setNewViewedIds((newViewedIds) => [
                    ...newViewedIds,
                    Number(record.id),
                  ])
                }
              },
            }
          }}
          rowClassName={(record) =>
            isAdminRole &&
            record['rowNotViewed'] &&
            !newViewedIds.includes(record.id as number)
              ? 'ant-table-highlighted-row'
              : ''
          }
        />
      </List>
      <EditInvoicePayable
        editingId={editingId}
        onClose={onClose}
        onComplete={refetchList}
        wiseButtonsAvailable={wiseButtonsAvailable}
      />
    </>
  )
}
